'use client'

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useSession, signOut } from 'next-auth/react'
import { Button } from '@/app/components/common/Button'
import { logError } from '@/lib/logging'

export function NavigationHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userZip, setUserZip] = useState<string | null>(null)
  const { data: session, status } = useSession()

  const handleLogout = () => {
    signOut({ callbackUrl: '/?logout=success' })
  }

  // Fetch user zip code when logged in
  useEffect(() => {
    if (status === 'authenticated' && session?.user) {
      const fetchUserProfile = async () => {
        try {
          const response = await fetch('/api/user/profile')
          if (response.ok) {
            const profile = await response.json()
            if (profile.zipCode) {
              setUserZip(profile.zipCode)
            }
          } else if (response.status === 401) {
            // User no longer exists in database - force sign out
            signOut({ callbackUrl: '/?logout=success' })
          }
        } catch (error) {
          logError('Failed to fetch user profile for zip code:', error)
        }
      }
      fetchUserProfile()
    }
  }, [session, status])

  // Build search URL with zip code if available
  const searchUrl = userZip ? `/search?zip=${encodeURIComponent(userZip)}` : '/search'

  // Determine dashboard URL based on user role
  const dashboardUrl = React.useMemo(() => {
    if (!session?.user) return '/dashboard'
    // Super users and users with admin/provider roles go to /admin
    if (session.user.isSuper) return '/admin'
    /* eslint-disable custom/no-inline-role-checks -- client-side routing with session data */
    const hasAdminOrProviderRole = session.user.orgs?.some(
      (org) => org.role === 'admin' || org.role === 'provider'
    )
    /* eslint-enable custom/no-inline-role-checks */
    return hasAdminOrProviderRole ? '/admin' : '/dashboard'
  }, [session])

  return (
    <header data-component-name="NavigationHeader" className="sticky top-0 z-50 bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link href="/" className="flex items-center">
            <Image
              src="/okkanti-logo-dark.svg"
              alt="Okkanti"
              width={158}
              height={24}
              className="h-6 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {session?.user ? (
              <>
                <Link
                  href={dashboardUrl}
                  className="text-gray-700 hover:text-primary transition-colors flex items-center text-xs"
                >
                  My Account
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-gray-700 hover:text-primary transition-colors flex items-center text-xs"
                >
                  Log Out
                </button>
              </>
            ) : (
              <Link
                href="/login"
                className="text-gray-700 hover:text-primary transition-colors flex items-center text-xs"
              >
                Log In
              </Link>
            )}
            <Link href={searchUrl}>
              <Button variant="primary" size="sm" className="flex items-center">
                Find Care
              </Button>
            </Link>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-primary focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t">
            <div className="flex flex-col space-y-4">
              {session?.user ? (
                <>
                  <Link
                    href={dashboardUrl}
                    className="text-gray-700 hover:text-primary transition-colors"
                  >
                    My Account
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-gray-700 hover:text-primary transition-colors text-left w-full"
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <Link
                  href="/login"
                  className="text-gray-700 hover:text-primary transition-colors text-center"
                >
                  Log In
                </Link>
              )}
              <Link href={searchUrl}>
                <Button variant="primary" size="sm" fullWidth>
                  Find Care
                </Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  )
}
